<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { __ } from '@oat-sa-private/ui-core';
    import { RadioGroup } from '@oat-sa-private/ui-elements';
    import StepperArray from '../../panel/a11y/controls/generic/StepperArray.svelte';
    import settingsKeys from '../../settings/settingsKeys.js';
    import { pitches, speeds, voices } from '@oat-sa/read-aloud-client/lib/preferences.js';
    import { untrack } from 'svelte';

    /**
     * @typedef {Object} Props
     * @property {Boolean} [disabled] - The UI component of the ReadAloud settings drawer
     * @property {Object} [readAloudSettings]
     * @property {Function} onChange
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        disabled = false,
        readAloudSettings = {},
        onChange
    } = $props();

    const voiceOptions = {
        [voices.male]: __('Male'),
        [voices.female]: __('Female')
    };

    /**
     * Voice speed options packed to apply constraints later
     */
    const allSpeedOptions = new Map([
        [speeds.slowest, __('slow')],
        [speeds.slow, __('medium slow')],
        [speeds.normal, __('default')],
        [speeds.fast, __('medium fast')],
        [speeds.fastest, __('fast')]
    ]);
    const allSpeedKeys = Array.from(allSpeedOptions.keys());

    const readAloudSettingsOnce = untrack(() => readAloudSettings);

    const speedKeys = readAloudSettingsOnce.constraints?.speed?.options
        ? allSpeedKeys.filter(option => readAloudSettingsOnce.constraints.speed.options.includes(option))
        : allSpeedKeys;

    /**
     * Voice pitch options packed to apply constraints later
     */
    const allPitchOptions = new Map([
        [pitches.lowest, __('low')],
        [pitches.medium, __('medium')],
        [pitches.highest, __('high')]
    ]);
    const allPitchKeys = Array.from(allPitchOptions.keys());
    const pitchKeys = readAloudSettingsOnce.constraints?.pitch?.options
        ? allPitchKeys.filter(option => readAloudSettingsOnce.constraints.pitch.options.includes(option))
        : allPitchKeys;

    // Initial values
    let voiceValue = readAloudSettingsOnce.toolState?.[settingsKeys.readAloudVoice] || voices.female;
    let speedValue = readAloudSettingsOnce.toolState?.[settingsKeys.readAloudSpeed] || speeds.normal;
    let pitchValue = readAloudSettingsOnce.toolState?.[settingsKeys.readAloudPitch] || pitches.medium;

    /**
     * Handle when user changed voice gender
     * @param {CustomEvent} e
     */
    function handleVoiceChange(e) {
        onChange?.({
            key: settingsKeys.readAloudVoice,
            value: e.detail?.value
        });
    }

    /**
     * Handle when user changed speed
     * @param {CustomEvent} e
     */
    function handleSpeedChange(e) {
        onChange?.({
            key: settingsKeys.readAloudSpeed,
            value: e.value
        });
    }

    /**
     * Handle when user changed pitch
     * @param {CustomEvent} e
     */
    function handlePitchChange(e) {
        onChange?.({
            key: settingsKeys.readAloudPitch,
            value: e.value
        });
    }
</script>

<style>
    .read-aloud-settings-drawer {
        --floating-bar-height: 5rem;

        padding-block-end: 0.25rem;
        padding-inline-start: 1.25rem;
        padding-inline-end: 1.75rem;
        margin: 0 0.25rem 0.25rem;
        background: var(--color-bg-inverted-secondary);
        border-bottom-left-radius: calc(var(--floating-bar-height) / 2);
        border-bottom-right-radius: calc(var(--floating-bar-height) / 2);

        & .control-row {
            display: flex;
            justify-content: space-between;
            align-items: center;

            & label {
                height: 5rem;
                display: flex;
                align-items: center;
                font-size: var(--fontsize-body-s);
            }
            & :global(.radio-group) {
                font-size: var(--fontsize-body-xs);

                & :global(.radio) {
                    margin-bottom: 0.25rem;

                    &:last-child {
                        margin-inline-end: 0;
                    }
                }
            }
            & :global(.icon-bar-btn) {
                background: inherit;

                &:hover,
                &:focus-visible {
                    background: var(--color-bg-inverted-secondary-hover);
                }
            }
        }
    }
</style>

<div class="read-aloud-settings-drawer">
    {#if !readAloudSettings.constraints?.voice?.disabled}
        <div class="control-row">
            <label for="voice">{__('Voice')}</label>
            <RadioGroup
                name="voice"
                value={voiceValue}
                options={voiceOptions}
                layout="grid"
                {disabled}
                on:change={handleVoiceChange} />
        </div>
    {/if}
    {#if !readAloudSettings.constraints?.speed?.disabled}
        <div class="control-row">
            <!-- svelte-ignore a11y_label_has_associated_control -->
            <label>{__('Speed')}</label>
            <StepperArray
                options={speedKeys}
                value={speedValue}
                ariaLabelDecr={__('decrease reading speed')}
                ariaLabelIncr={__('increase reading speed')}
                {disabled}
                onChange={handleSpeedChange} />
        </div>
    {/if}
    {#if !readAloudSettings.constraints?.pitch?.disabled}
        <div class="control-row">
            <!-- svelte-ignore a11y_label_has_associated_control -->
            <label>{__('Pitch')}</label>
            <StepperArray
                options={pitchKeys}
                value={pitchValue}
                ariaLabelDecr={__('decrease reading pitch')}
                ariaLabelIncr={__('increase reading pitch')}
                {disabled}
                onChange={handlePitchChange} />
        </div>
    {/if}
</div>
